// İSG uzmanı portalı — İzin detayı: Ek Belge Talepleri (api-contract §30.10). İznin permit tipinde
// istenen §22 standart belgelerine **EK** olarak İSG/yetkili, iznin detayından bir belge türü talep
// eder. Talep **permit-seviyesidir** (izindeki TÜM personele uygulanır) ve **readiness'e katılır**
// (personel `missingDocumentTypes` birleşik kümeden okunur — tek kaynak). Bu yüzden ekle/kaldır
// başarısında izin detayı (`GET /permits/{uuid}`) invalidate edilir (missingDocumentTypes değişir).
//
// Listele: `documentRequests[]` — belge türü (§27 kayıt etiketi; pasif tür dahil, yoksa ham kod) + not +
// talep eden + tarih + **kaldır**. Ekle: belge türü seç (`selectable('personnel')` — kaydın aktif
// personel türleri) + opsiyonel not → `POST /permits/{uuid}/document-requests { documentType, note? }`.
// Kaldır: onay (confirm) → `DELETE .../document-requests/{requestId}`. Ekle/kaldır **yalnız düzenleme
// yetkisi** olanda (detayın `canEdit`/reviewer deseni; yoksa salt-okunur liste). Tür reddi
// (`INVALID`/`INACTIVE`, alan `documentType`, §27.6) → kayıt tazelenir, seçim temizlenir, alan hatası;
// diğer 400/409/404 → `resolveApiErrorMessage` ile kullanıcı-dostu mesaj (ham kod sızmaz).

import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  Select,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  Tag,
  type WorkPermitDocumentRequestDto,
  currentCulture,
  isTypeFieldRejection,
  permitsApi,
  resolveApiErrorMessage,
  useDocumentTypes,
  useRefetchDocumentTypesOnMount,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import { permitsKeys } from '../../lib/permitsKeys';
import { TabPanelIntro } from '../TabPanelIntro';

interface PermitDocumentRequestsPanelProps {
  permitUuid: string;
  /** İznin ek belge talepleri (§30.10; yoksa `[]`). */
  documentRequests: WorkPermitDocumentRequestDto[];
  /**
   * Düzenleme yetkisi (detayın `canEdit`/reviewer deseni — §30.4). `true` → ekle/kaldır;
   * `false` → salt-okunur liste (yetki backend'de son kapıdır; bu yalnız UX görünürlüğü).
   */
  canEdit: boolean;
}

const noteInputClass =
  'flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg ' +
  'placeholder:text-muted transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-60';

export function PermitDocumentRequestsPanel({
  permitUuid,
  documentRequests,
  canEdit,
}: PermitDocumentRequestsPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <TabPanelIntro>{t('permits.docRequests.subtitle')}</TabPanelIntro>

      {/* Ekle — yalnız düzenleme yetkisi olanda (salt-okunur ise ipucu). */}
      {canEdit ? (
        <DocumentRequestComposer permitUuid={permitUuid} existing={documentRequests} />
      ) : (
        <Alert tone="info">{t('permits.docRequests.readOnlyHint')}</Alert>
      )}

      <Table caption={t('permits.detail.section.documentRequests')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('permits.docRequests.typeLabel')}</TableHead>
            <TableHead>{t('permits.docRequests.note')}</TableHead>
            <TableHead>{t('permits.docRequests.col.requested')}</TableHead>
            {canEdit ? (
              <TableHead className="w-12">
                <span className="sr-only">{t('permits.common.actions')}</span>
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {documentRequests.length === 0 ? (
            <TableMessageRow colSpan={canEdit ? 4 : 3}>
              <EmptyState icon={FileText} title={t('permits.docRequests.empty')} />
            </TableMessageRow>
          ) : (
            documentRequests.map((request) => (
              <DocumentRequestRow
                key={request.uuid}
                permitUuid={permitUuid}
                request={request}
                canEdit={canEdit}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

const TYPE_ERROR_ID = 'doc-request-type-error';

/**
 * Ek belge talebi ekleme kutusu (İSG uzmanı; §30.10). Belge türü seçici §27 kaydının AKTİF personel
 * türleridir (`selectable('personnel')`); zaten aktif talebi olan türler listelenmez (mükerrer engeli
 * UX tarafında; backend yine `409 DUPLICATE_DOCUMENT_REQUEST` son kapıdır, edge → dostu mesaj). Not
 * opsiyonel. Başarıda izin detayı invalidate (readiness değişir). Seçici durumları `DocumentTypeField`
 * ile aynıdır (Review Focus 2): yükleniyor → devre dışı + "Yükleniyor…"; hata yalnız `isError && !types`;
 * kayıt bu sekmeye her girişte bir kez tazelenir.
 */
function DocumentRequestComposer({
  permitUuid,
  existing,
}: {
  permitUuid: string;
  existing: WorkPermitDocumentRequestDto[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  useRefetchDocumentTypesOnMount();
  const registry = useDocumentTypes();
  const [documentType, setDocumentType] = useState('');
  const [note, setNote] = useState('');
  const [typeError, setTypeError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  // Zaten aktif talebi olan türler listelenmez (mükerrer engeli UX; §30.10).
  const requestedTypes = new Set(existing.map((r) => r.documentType));
  const selectable = registry.selectable('personnel');
  const options: SelectOption[] = selectable
    .filter((type) => !requestedTypes.has(type.code))
    .map((type) => ({ value: type.code, label: registry.label('personnel', type.code) }));
  const loading = registry.isPending;
  const failed = registry.isError && registry.types === undefined;

  const mutation = useMutation({
    mutationFn: (body: { documentType: string; note: string | null }) =>
      permitsApi.addPermitDocumentRequest(permitUuid, body),
    onSuccess: () => {
      // Ek talep readiness'e katılır → personel missingDocumentTypes değişir (tek kaynak).
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setDocumentType('');
      setNote('');
    },
    // §27.6: kayıt tazelenir, seçim temizlenir, alan hatası; aynı gövde yeniden gönderilmez.
    onError: (error) => {
      if (!isTypeFieldRejection(error)) {
        return;
      }
      void registry.refetch();
      setDocumentType('');
      setTypeError(t('documentTypes.typeRejected'));
    },
  });

  // Ret sonrası odak seçiciye — istek sürerken devre dışıdır, bu yüzden bitince.
  useEffect(() => {
    if (typeError !== null && !mutation.isPending) {
      selectRef.current?.focus();
    }
  }, [typeError, mutation.isPending]);

  // Kayıt yüklü ama sunulacak tür yok: aktif personel türü hiç yoksa kayıt açıklaması, hepsinin
  // aktif talebi varsa mevcut ipucu (yeni tür kalmadı).
  if (registry.types !== undefined && options.length === 0) {
    return (
      <p className="border-b border-border px-1 py-2 text-sm text-muted">
        {selectable.length === 0 ? t('documentTypes.empty') : t('permits.docRequests.allRequested')}
      </p>
    );
  }

  const trimmedNote = note.trim();

  return (
    <form
      // Kutulu (`border-dashed`) şerit KALKAR — PPE'nin AYNI notu (`PermitProtectiveEquipmentPanel
      // .tsx`), Tile'a bitişik bir üst şerit.
      className="flex flex-col gap-2 border-b border-border px-1 py-2 carbon:px-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (documentType !== '' && !mutation.isPending) {
          setTypeError(null);
          mutation.mutate({
            documentType,
            note: trimmedNote.length > 0 ? trimmedNote : null,
          });
        }
      }}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor="doc-request-type" className="text-xs font-medium text-muted">
            {t('permits.docRequests.typeLabel')}
          </label>
          <Select
            ref={selectRef}
            id="doc-request-type"
            options={options}
            placeholder={
              loading ? t('documentTypes.loading') : t('permits.docRequests.typePlaceholder')
            }
            value={documentType}
            disabled={loading || failed || mutation.isPending}
            aria-busy={loading || undefined}
            invalid={typeError !== null}
            aria-describedby={typeError !== null ? TYPE_ERROR_ID : undefined}
            onChange={(e) => {
              setDocumentType(e.target.value);
              setTypeError(null);
            }}
          />
          {typeError !== null ? (
            <p id={TYPE_ERROR_ID} role="alert" className="text-xs font-medium text-danger">
              {typeError}
            </p>
          ) : null}
        </div>
        <div className="flex flex-[2] flex-col gap-1">
          <label htmlFor="doc-request-note" className="text-xs font-medium text-muted">
            {t('permits.docRequests.note')}
          </label>
          <input
            id="doc-request-note"
            type="text"
            className={noteInputClass}
            placeholder={t('permits.docRequests.notePlaceholder')}
            value={note}
            disabled={mutation.isPending}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          variant="ghost"
          size="sm"
          className="sm:mt-5 sm:self-start"
          loading={mutation.isPending}
          disabled={documentType === '' || mutation.isPending}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('permits.docRequests.add')}
        </Button>
      </div>
      {failed ? (
        <Alert tone="danger">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{t('documentTypes.loadError')}</span>
            <Button variant="ghost" size="sm" onClick={() => void registry.refetch()}>
              {t('documentTypes.retry')}
            </Button>
          </div>
        </Alert>
      ) : null}
      {/* Tür reddi seçicinin altında gösterilir; genel Alert onu yinelemez. */}
      {mutation.isError && !isTypeFieldRejection(mutation.error) ? (
        <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
      ) : null}
    </form>
  );
}

/**
 * Tek ek belge talebi satırı — belge türü (§27 kayıt etiketi) + not + talep eden + tarih + kaldır
 * (onay diyaloğuyla; yalnız düzenleme yetkisi olanda). Kaldırma başarısında izin detayı invalidate
 * (readiness yeniden hesaplanır — §30.10).
 */
function DocumentRequestRow({
  permitUuid,
  request,
  canEdit,
}: {
  permitUuid: string;
  request: WorkPermitDocumentRequestDto;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const [removeOpen, setRemoveOpen] = useState(false);

  const typeLabel = label('personnel', request.documentType);

  const removeMutation = useMutation({
    mutationFn: () => permitsApi.removePermitDocumentRequest(permitUuid, request.uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setRemoveOpen(false);
    },
  });

  return (
    <TableRow>
      <TableCell
        truncate={false}
        className="break-words font-medium text-fg carbon:h-auto carbon:py-2"
      >
        {typeLabel}
      </TableCell>
      <TableCell className="text-muted">
        <div className="flex flex-col gap-1">
          {request.note ?? '—'}
          {removeMutation.isError ? (
            <span className="text-xs text-danger">
              {resolveApiErrorMessage(removeMutation.error, t)}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <Tag color="gray">
          {request.requestedByName
            ? t('permits.docRequests.requestedBy', {
                name: request.requestedByName,
                date: formatDateTime(request.requestedAt, culture),
              })
            : formatDateTime(request.requestedAt, culture)}
        </Tag>
      </TableCell>
      {canEdit ? (
        <TableCell className="w-12 px-2">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('permits.docRequests.remove')}
            disabled={removeMutation.isPending}
            onClick={() => setRemoveOpen(true)}
          >
            <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
          </Button>
          <ConfirmDialog
            open={removeOpen}
            onOpenChange={(open) => !open && setRemoveOpen(false)}
            title={t('permits.docRequests.remove.title')}
            body={t('permits.docRequests.remove.body', { type: typeLabel })}
            confirmLabel={t('permits.docRequests.remove.confirm')}
            confirmVariant="danger"
            loading={removeMutation.isPending}
            error={removeMutation.isError ? removeMutation.error : undefined}
            onConfirm={() => removeMutation.mutate()}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
}
